import React, {useEffect} from 'react';
import Table from '@amzn/awsui-components-react/polaris/table';
import Box from '@amzn/awsui-components-react/polaris/box';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import {getUserName} from '../../../auth/MidwayJwtToken';
import {TestRequest} from '../model/TestRequest';
import {useCollection} from '@amzn/awsui-collection-hooks';
import {
    createTableSortLabelFn,
    getHeaderCounterText,
    getTextFilterCounterText,
    TableEmptyState,
    TableNoMatchState,
    TableWithFullPageHeader
} from '../../../util/TableUtils';
import TextFilter from '@amzn/awsui-components-react/polaris/text-filter';
import {isFromAccountingTeam, isFromTechTeam} from '../../../auth/UserPermissions';
import PermissionsModal from '../../PermissionsModal';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import {TestingStatus} from '../model/TestingStatus';

const PAGE_SIZE: number = 10;

const COLUMN_DEFINITIONS = [
    {
        id: "serial_number",
        header: "#",
        cell: (item: TestRequest) => item.serialNumber,
        sortingField: "serial_number",
    },
    {
        id: "request_uid",
        header: "ID",
        cell: (item: TestRequest) => item.identifier,
        sortingField: "request_uid",
    },
    {
        id: "user_login",
        sortingField: "user_login",
        header: "User Login",
        cell: (item: TestRequest) => item.userLogin,
    },
    {
        id: "test_purpose",
        sortingField: "test_purpose",
        header: "Test Purpose",
        cell: (item: TestRequest) => item.testPurpose,
    },
    {
        id: "test_request_description",
        sortingField: "test_request_description",
        header: "Test Request Description",
        cell: (item: TestRequest) => item.requestDescription,
    },
    {
        id: "current_status",
        sortingField: "current_status",
        header: "Current Status",
        cell: (item: TestRequest) => TestingStatus[item.currentStatus],
    },
    {
        id: "creation_date",
        sortingField: "creation_date",
        header: "Creation Date",
        cell: (item: TestRequest) => item.creationDate,
    },
    {
        id: "last_updated_date",
        sortingField: "last_updated_date",
        header: "Last Updated Date",
        cell: (item: TestRequest) => item.lastUpdatedDate,
    },
]

export default function TestRequestsTable(props) {
    const [hasAccess] = React.useState(isFromTechTeam() || isFromAccountingTeam());

    const selectedItems = () => {
        const items = collectionProps.selectedItems;

        if (items) {
            return items;
        }

        return [];
    }

    const getSelectedItem = () => {
        const items = collectionProps.selectedItems;
        if (items && items.length > 0) {
            return items[0] as TestRequest;
        }
        else {
            return {} as TestRequest;
        }
    }


    const columnDefinitions = COLUMN_DEFINITIONS.map(column => {
        return ({...column, ariaLabel: createTableSortLabelFn(column)});
    })

    const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
        props.testItems,
        {
            filtering: {
                empty: <TableEmptyState resourceName="Tests" />,
                noMatch: <TableNoMatchState onClearFilter={() => actions.setFiltering('')} />,
            },
            pagination: { pageSize: PAGE_SIZE },
            sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
            selection: {},
        }
    );

    // update the parent state for which item is selected
    useEffect(() =>
        {
            props.setSelectedItem(getSelectedItem());
        }, [collectionProps.selectedItems]
    );


    return (
        <Box>
            {!hasAccess && <PermissionsModal hasPageAccess={hasAccess}/>}
            {hasAccess && <SpaceBetween direction='vertical' size='xl'>
                <Table
                    {...collectionProps}
                    columnDefinitions={columnDefinitions}
                    enableKeyboardNavigation={true}
                    items={props.testItems}
                    stickyHeader
                    selectionType='single'
                    loadingText={"Fetching User Tests for: " + getUserName()}
                    resizableColumns={true}
                    header={
                        <TableWithFullPageHeader
                            title={"Test Requests"}
                            createButtonText={"Create New Request"}
                            selectedItemsCount={selectedItems().length}
                            counter={getHeaderCounterText(props.testItems, selectedItems())}
                            onCreateButtonClick={props.createNewTestItemButtonClick}
                            onViewDetailsButtonClick={props.viewDetailsButtonClick}
                            disableEdit={true}
                            disableDelete={true}
                        />
                    }
                    filter={
                        <TextFilter
                            {...filterProps}
                            filteringAriaLabel="Filter Test Requests"
                            filteringPlaceholder="Find Test Requests"
                            filteringClearAriaLabel="Clear"
                            countText={getTextFilterCounterText(filteredItemsCount)}
                        />
                    }
                    pagination={<Pagination {...paginationProps} />}
                    empty={
                        <Box textAlign="center" color="inherit">
                            <Box
                                padding={{ bottom: "s" }}
                                variant="p"
                                color="inherit"
                            >
                                No Tests to display
                            </Box>
                        </Box>
                    }
                />
            </SpaceBetween>}
        </Box>
    );
}